import React from 'react';
import brandConfig from '../../config/brandConfig';
import { Award, Shield, Target, Heart } from 'lucide-react';

const AboutSection = () => {
  const values = [
    { icon: <Shield className="w-7 h-7" />, title: "Transparencia", description: "Todas nuestras transacciones son transparentes y seguras.", gradient: "from-blue-500 to-cyan-500" },
    { icon: <Target className="w-7 h-7" />, title: "Compromiso", description: "Comprometidos con la excelencia en cada proyecto.", gradient: "from-orange-500 to-amber-500" },
    { icon: <Heart className="w-7 h-7" />, title: "Confianza", description: "Construimos relaciones duraderas basadas en la confianza.", gradient: "from-rose-500 to-pink-500" }
  ];

  const stats = [
    { number: "10+", label: "Anos de experiencia" },
    { number: "150+", label: "Inmobiliarias asociadas" },
    { number: "2,500+", label: "Propiedades publicadas" },
    { number: "15,000+", label: "Clientes satisfechos" }
  ];

  return (
    <section id="about" className="py-24 lg:py-32 bg-gray-50 dark:bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <p className="text-sm font-bold uppercase tracking-[0.2em] mb-3" style={{ color: brandConfig.primaryColor }}>
            Nuestra Historia
          </p>
          <h2 className="text-4xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 tracking-tight">
            Sobre
            <span className="font-display italic" style={{ color: brandConfig.primaryColor }}> Nosotros</span>
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            {brandConfig.domain} es la plataforma inmobiliaria lider en Latinoamerica, conectando inmobiliarias certificadas
            con compradores para transacciones seguras y exitosas en toda la region.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 lg:p-10 border border-gray-100 dark:border-gray-800 card-hover group">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl mb-6 text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Target className="w-7 h-7" />
            </div>
            <h3 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-4 tracking-tight">
              Nuestra Mision
            </h3>
            <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
              Facilitar el acceso a propiedades de calidad en toda Latinoamerica, proporcionando herramientas
              innovadoras y un servicio excepcional que beneficie tanto a inmobiliarias como a compradores.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 lg:p-10 border border-gray-100 dark:border-gray-800 card-hover group">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl mb-6 text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Award className="w-7 h-7" />
            </div>
            <h3 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-4 tracking-tight">
              Nuestra Vision
            </h3>
            <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
              Ser la plataforma inmobiliaria mas confiable y utilizada en Latinoamerica, reconocida
              por nuestra innovacion, transparencia y compromiso con la excelencia.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="mb-20">
          <h3 className="text-3xl font-extrabold text-center text-gray-900 dark:text-white mb-12 tracking-tight">
            Nuestros Valores
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-800 text-center card-hover group"
              >
                <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${value.gradient} rounded-2xl mb-5 text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {value.icon}
                </div>
                <h4 className="text-xl font-extrabold text-gray-900 dark:text-white mb-3 tracking-tight">{value.title}</h4>
                <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="relative rounded-3xl p-10 lg:p-14 text-white overflow-hidden grain-overlay" style={{
          background: 'linear-gradient(135deg, #102a43 0%, #243b53 50%, #102a43 100%)'
        }}>
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, ${brandConfig.primaryColor}40 0%, transparent 50%), radial-gradient(circle at 80% 50%, ${brandConfig.secondaryColor}30 0%, transparent 50%)`
          }} />
          <div className="relative z-10">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-extrabold mb-3 tracking-tight">Numeros que Hablan</h3>
              <p className="text-white/50 max-w-2xl mx-auto">
                Mas de una decada de experiencia respaldando transacciones inmobiliarias exitosas
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center glass rounded-2xl p-6">
                  <div className="text-3xl lg:text-4xl font-extrabold text-white mb-1 tracking-tight">{stat.number}</div>
                  <div className="text-white/50 text-sm font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
